import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuickActionsQuery } from '@/hooks/queries';
import { quickActionsService } from "@flowstate/quick-actions";
import type { QuickAction, QuickActionGroup, SystemState } from '../types/quickActions';
import { logger } from "@/lib/logger";

interface UseQuickActionsOptions {
  systemState?: SystemState; // Legacy - no longer used, kept for backward compatibility
  enableAutoRefresh?: boolean; // Legacy - auto-refresh is now handled by TanStack Query
  refreshInterval?: number; // Legacy - intervals are now handled by TanStack Query
}

interface UseQuickActionsResult {
  actions: QuickAction[];
  groupedActions: QuickActionGroup[];
  searchActions: (query: string) => QuickAction[];
  executeAction: (action: QuickAction, onExecute: (prompt: string) => void) => void;
  refreshActions: () => void;
  isLoading: boolean;
  recentActions: QuickAction[];
  addToRecent: (action: QuickAction) => void;
  clearRecent: () => void;
}

const RECENT_ACTIONS_KEY = 'aiNexus.quickActions.recent';
const MAX_RECENT_ACTIONS = 5;

/**
 * Hook for managing context-aware quick actions
 * 
 * MIGRATED TO TANSTACK QUERY (2026-01-27)
 * Now uses useQuickActionsQuery for automatic caching, refetching, and error handling.
 * Maintains backward-compatible API for existing components.
 */
export function useQuickActions(_options: UseQuickActionsOptions = {}): UseQuickActionsResult {
  // Legacy options are ignored - TanStack Query handles caching/refetching now
  const [recentActionIds, setRecentActionIds] = useState<string[]>([]);

  // Use TanStack Query hook for actions
  const {
    actions,
    groupedActions,
    isLoading,
    refetch
  } = useQuickActionsQuery();

  // Load recent action IDs from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_ACTIONS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Handle migration from old format (array of objects) to new format (array of IDs)
        if (Array.isArray(parsed)) {
          if (parsed.length > 0 && typeof parsed[0] === 'string') {
            // New format: array of IDs
            setRecentActionIds(parsed);
          } else if (parsed.length > 0 && typeof parsed[0] === 'object') {
            // Old format: array of action objects - extract IDs and migrate
            const ids = parsed.map(action => action.id).filter(Boolean);
            setRecentActionIds(ids);
            localStorage.setItem(RECENT_ACTIONS_KEY, JSON.stringify(ids));
          }
        }
      }
    } catch (error) {
      logger.warn('Failed to load recent actions:', { error });
      // Clear corrupted localStorage
      localStorage.removeItem(RECENT_ACTIONS_KEY);
    }
  }, []);

  // Reconstruct recent actions from IDs by looking them up in the current actions array
  const recentActions = useMemo(() => {
    return recentActionIds
      .map(id => actions.find(action => action.id === id))
      .filter((action): action is QuickAction => action !== undefined);
  }, [recentActionIds, actions]);

  // Search actions
  const searchActions = useCallback((query: string) => {
    return quickActionsService.searchActions(actions, query);
  }, [actions]);

  // Add action to recent list
  const addToRecent = useCallback((action: QuickAction) => {
    setRecentActionIds(prev => {
      // Remove action ID if it already exists
      const filtered = prev.filter(id => id !== action.id);
      
      // Add to beginning and limit to MAX_RECENT_ACTIONS
      const updated = [action.id, ...filtered].slice(0, MAX_RECENT_ACTIONS);
      
      // Save to localStorage (only IDs, not full action objects)
      try {
        localStorage.setItem(RECENT_ACTIONS_KEY, JSON.stringify(updated));
      } catch (error) {
        logger.warn('Failed to save recent actions:', { error });
      }
      
      return updated;
    });
  }, []);

  // Execute action
  const executeAction = useCallback((action: QuickAction, onExecute: (prompt: string) => void) => {
    // Add to recent actions
    addToRecent(action);
    
    // Map domain/category to specialist tags if needed
    let finalPrompt = action.prompt;
    
    // If the action has a specific domain that maps to a specialist, ensure the tag is present
    // This helps the backend PlanNode route correctly
    const specialistTags: Record<string, string> = {
      'web-search': '[search web]',
      'research': '[search web]',
      'task-enhancement': '[enhance task]',
      'brain-dump': '[process brain dump]',
      'scheduling': '[schedule tasks]',
      'polishing': '[polish task]'
    };

    const tag = (action.domain && specialistTags[action.domain]) || (action.category && specialistTags[action.category]);
    if (tag && !finalPrompt.includes(tag)) {
      finalPrompt = `${tag} ${finalPrompt}`;
    }
    
    // Execute the action by sending prompt to chat
    onExecute(finalPrompt);
    
    // Log for analytics
    logger.info('Quick action executed:', {
      id: action.id,
      label: action.label,
      category: action.category,
      domain: action.domain,
      tagged: !!tag
    });
  }, [addToRecent]);

  // Clear recent actions
  const clearRecent = useCallback(() => {
    setRecentActionIds([]);
    try {
      localStorage.removeItem(RECENT_ACTIONS_KEY);
    } catch (error) {
      logger.warn('Failed to clear recent actions:', { error });
    }
  }, []);

  // Refresh actions - now delegates to TanStack Query refetch
  const refreshActions = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    actions,
    groupedActions,
    searchActions,
    executeAction,
    refreshActions,
    isLoading,
    recentActions,
    addToRecent,
    clearRecent
  };
}
